// imports section Start
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Toolbar,
  Button,
} from "@mui/material";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { GUEST_API_02, ORDER_API_05, ORDER_API_09 } from "../../api/ApiModel";
import { useSelector } from "react-redux";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  MSG_CASHIER_FAILED,
  MSG_CASHIER_SUCCESS,
} from "../../utils/sys/Messages";
import SnackbarMessage from "../common/SnackbarMessage";
import {
  CON_IS_CREDIT_NO,
  CON_VALUE_ZERO_ST,
  CON_ADDON_CODE,
  CON_IS_ADDON,
  CON_IS_CASHIER_YES,
  CON_DELIVERY_STS,
  CON_GUEST_REF_CODE,
  CON_STATUS_PENDING,
  CON_EMPTY_VALUE,
  CON_VALUE_ZERO,
  CON_IS_CASHIER_NO,
  CON_STATUS_ACTIVE,
  CON_TAX_PAGE_CODE,
  CON_ORDERTYPE_ONSHOP,
} from "../../utils/sys/Constants";
import useClientIP from "../../utils/common/ClientIP";
// imports section End

function OfficerManageOrders({ orderDetails, componentVisibility }) {
  // Hooks start

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const styles = useStyles(theme);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Control Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [isComponentVisible, setIsComponentVisible] = useState(true);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [orderDetailsData, setOrderDetailsData] = useState([]);
  const [responseValues, setResponseValues] = useState({
    advance: 0,
    afterCharge: 0,
    balance: 0,
    bank: 0,
    cash: 0,
    charge: 0,
    discount: 0,
    gift: 0,
    gross: 0,
    loyalty: 0,
    nettGross: 0,
    refundAmt: 0,
    shipping: 0,
    tax: 0,
    totNett: 0,
    totPaid: 0,
  });
  const [table1Data, setTable1Data] = useState({});
  const [table2Data, setTable2Data] = useState([]);
  const [table3Data, setTable3Data] = useState([]);
  const clientIP = useClientIP();

  // Fetch order details when the `orderDetails` prop updates
  useEffect(() => {
    if (orderDetails?.ORDERREFCODE) {
      fetchOrderDetails(orderDetails.ORDERREFCODE);
    }
  }, [orderDetails]);

  const fetchOrderDetails = async (orderRefCode) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await externalgetApi(GUEST_API_02, "get", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        ORDERREFCODE: orderRefCode,
        STS: CON_STATUS_ACTIVE,
      });

      // Set response to state
      setOrderDetailsData(response.sort((a, b) => a.KOTSEQCODE - b.KOTSEQCODE));

      // Map the response to rows for the table
      const mappedRows = response.map((item, index) => ({
        id: index + 1,
        menuName: item.MENUNAME || "N/A",
        qty: item.QTY || "N/A",
        rate: item.RATE || "N/A",
        amount: calculateTotalAmount(item) || "N/A",
        remark: item.REMARK || "N/A",
      }));
      console.log("i order details officer", response);
      setRows(mappedRows);
      setIsComponentVisible(true);
    } catch (err) {
      setError(err.message || "Failed to fetch order details");
    } finally {
      setIsLoading(false);
    }
  };
  // Hooks end
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  // Handlers start
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  const calculateGrossAmount = (data) => {
    return data.reduce((total, item) => total + calculateTotalAmount(item), 0);
  };

  const calculateTotalAmount = (item) => {
    // Calculate the total amount by multiplying quantity and rate
    const quantity = item.QTY || 0; // Ensure quantity defaults to 0 if undefined
    const rate = item.RATE || 0; // Ensure rate defaults to 0 if undefined
    return quantity * rate; // Multiply and return the total amount
  };
  const grossAmount = calculateGrossAmount(orderDetailsData);

  // Sort and map data after setting state
  // const sortedData = orderDetailsData.sort(
  //   (a, b) => a.KOTSEQCODE - b.KOTSEQCODE
  // );

  // Handlers end

  // API calls start
  // Function to fetch order details

  const mappedDtDetail = orderDetailsData.map((item) => ({
    kotseqcode: item.KOTSEQCODE || "",
    addoncode: CON_ADDON_CODE,
    menucatcode: String(item.MENUCATCODE) || "",
    menucode: String(item.MENUCODE) || "",
    qty: item.QTY || 0,
    rate: item.RATE || 0,
    amount: calculateTotalAmount(item),
    remark: String(item.REMARK) || "",
    isaddon: CON_IS_ADDON,
    sts: CON_STATUS_PENDING,
  }));

  const handlePlaceOrder = async () => {
    setLoading(true);
    const dtTax =
      Array.isArray(table2Data) && table2Data.length > 0
        ? table2Data.map((taxItem) => ({
            taxpgcode: taxItem.TAXPAGECODE ? String(taxItem.TAXPAGECODE) : "0", // default to "0" if empty
            compcode: taxItem.COMPCODE ? String(taxItem.COMPCODE) : "0", // default to "0" if empty
            loccode: taxItem.LOCCODE ? String(taxItem.LOCCODE) : "0", // default to "0.00" if empty
            taxcode: taxItem.TAXCODE ? String(taxItem.TAXCODE) : "0",
            taxrate: taxItem.TAXRATE || 0,
            amount: taxItem.TAXAMOUNT || 0, // default to "0.00" if empty
          }))
        : [
            // If no data exists, send a single item with default empty values
            {
              taxpgcode: "0", // default empty value
              compcode: "0", // default empty value
              loccode: "0", // default empty value
              taxcode: "0",
              taxrate: 0,
              amount: 0,
            },
          ];

    // Map the data into the required format for dtDetail
    const mappedDtDetail = orderDetailsData.map((item) => ({
      kotseqcode: item.KOTSEQCODE || "",
      addoncode: CON_ADDON_CODE,
      menucatcode: String(item.MENUCATCODE) || "",
      menucode: String(item.MENUCODE) || "",
      qty: item.QTY || 0,
      rate: item.RATE || 0,
      amount: calculateTotalAmount(item),
      remark: String(item.REMARK) || "",
      isaddon: CON_IS_ADDON,
      sts: CON_STATUS_PENDING,
    }));
    // const grossAmount = mappedDtDetail.reduce(
    //   (total, item) => total + item.amount,
    //   0
    // );
    const dtCharges =
      Array.isArray(table3Data) && table3Data.length > 0
        ? table3Data.map((chargeItem) => ({
            ordertypecode: chargeItem.ORDERTYPECODE
              ? String(chargeItem.ORDERTYPECODE)
              : "0", // default to "0" if empty
            chargecode: chargeItem.CHARGECODE
              ? String(chargeItem.CHARGECODE)
              : "0", // default to "0" if empty
            chargerate: chargeItem.CHARGERATE || 0, // default to "0.00" if empty
            amount: chargeItem.CHARGEAMOUNT || 0, // default to "0.00" if empty
          }))
        : [
            // If no data exists, send a single item with default empty values
            {
              ordertypecode: "0", // default empty value
              chargecode: "0", // default empty value
              chargerate: 0, // default empty value
              amount: 0, // default empty value
            },
          ];

    const payload = {
      // Array of order items in dtDetail
      dtDetail: mappedDtDetail,

      dtTax,
      dtCharges,

      dtPay: [
        {
          paymode: CON_EMPTY_VALUE,
          clientname: CON_EMPTY_VALUE,
          refno: CON_EMPTY_VALUE,
          bank: CON_EMPTY_VALUE,
          branch: CON_EMPTY_VALUE,
          amount: CON_VALUE_ZERO,
        },
      ],

      dtVoucher: [
        {
          refcode: CON_EMPTY_VALUE,
          vouchercode: CON_EMPTY_VALUE,
          voucherno: CON_EMPTY_VALUE,
          barcode: CON_EMPTY_VALUE,
          amount: CON_VALUE_ZERO,
        },
      ],
       dtagentdet: [
        {
            orderagentcode: "",
      amount: 0,
      agentfees: "",
      remark: "",
      sts: "A"
        }
       ],

      // Main Order Data
      compcode: COMPCODE,
      loccode: LOCCODE,
      restcode: RESTCODE,
      ordercode: CON_EMPTY_VALUE,
      guestordercode: String(orderDetails.ORDERREFCODE),
      terminalcode: CON_VALUE_ZERO_ST,
      paymentcode: CON_EMPTY_VALUE,
      guestrefcode: CON_GUEST_REF_CODE,
      tablecode: String(orderDetails.TABLECODE),
      orderendate: new Date().toISOString().split("T")[0],
      deliverydate: CON_EMPTY_VALUE,
      deliverytime: CON_EMPTY_VALUE,
      billamount: table1Data.TOTNETT || CON_VALUE_ZERO,
      discount: CON_VALUE_ZERO_ST,
      paidamount: CON_VALUE_ZERO_ST,
      advanceamount: CON_VALUE_ZERO_ST,
      shippingamount: CON_VALUE_ZERO_ST,
      balanceamount: CON_VALUE_ZERO_ST,
      remark: CON_EMPTY_VALUE,
      ordersts: CON_STATUS_PENDING,
      orderdetsts: CON_STATUS_ACTIVE,
      paymentsts: CON_STATUS_PENDING,
      deliverysts: CON_DELIVERY_STS,
      officername: CON_EMPTY_VALUE,
      enuser: String(USERNAME),
      locdate: new Date().toISOString().split("T")[0],
      roomnocode: CON_EMPTY_VALUE,
      ordertype: CON_ORDERTYPE_ONSHOP,

      iscashier: CON_IS_CASHIER_YES,
      paymode: CON_EMPTY_VALUE,
      paydate: new Date().toISOString().split("T")[0],
      grossamount: String(grossAmount),
      giftamount: CON_VALUE_ZERO_ST,
      loyaltyamount: CON_VALUE_ZERO_ST,
      taxamount: String(table1Data.TAX) || CON_VALUE_ZERO_ST,
      cashamount: CON_VALUE_ZERO_ST,
      chequeamount: CON_VALUE_ZERO_ST,
      slipamount: CON_VALUE_ZERO_ST,
      cardamount: CON_VALUE_ZERO_ST,
      discountrate: CON_VALUE_ZERO_ST,
      creditdueamount: CON_VALUE_ZERO_ST,
      adjustamount: CON_VALUE_ZERO_ST,
      AGENTREMARK: "",
      AGENTCODE: "0",
      iscredit: CON_IS_CREDIT_NO,
      isplacedorder: false,
      ipaddress: clientIP,
    };
    try {
      console.log("Place order payload", payload);
      const response = await externalpostApi(ORDER_API_05, payload);
      const orderCode = response;
      setIsComponentVisible(false);
      setSnackbarMessage(MSG_CASHIER_SUCCESS);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(MSG_CASHIER_FAILED);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handleOrderCalculation = async () => {
      const payload = {
        taxpagecode: CON_TAX_PAGE_CODE,
        compcode: COMPCODE,
        loccode: LOCCODE,
        restcode: RESTCODE,
        ordertype: CON_ORDERTYPE_ONSHOP,
        gross: parseFloat(calculateGrossAmount(orderDetailsData)) || 0, // Backend value
        adjustment: 0, // You can set this to the calculated adjustment if needed
        totbank: 0, // You can set this based on the required calculation
        discountrate: 0, // Same for discount rate if applicable
        totgift: 0, // Add logic if needed for gift amounts
        totloyalty: 0, // Same for loyalty points
        totcash: 0, // User input value
        shipping: 0, // If there's a shipping charge, set here
        advance: 0, // User input value
        refundamt: 0,
      };

      try {
        // Call the external API
        const response = await externalpostApi(ORDER_API_09, payload);
        if (response && response.Table1 && response.Table2 && response.Table3) {
          setTable1Data(response.Table1[0] || {});
          setTable2Data(response.Table2 || []);
          setTable3Data(response.Table3 || []);

          // Update state with calculation results if needed
          setResponseValues({
            advance: response.Table1[0]?.ADVANCE || 0,
            afterCharge: response.Table1[0]?.AFTERCHARGE || 0,
            balance: response.Table1[0]?.BALANCE || 0,
            bank: response.Table1[0]?.BANK || 0,
            cash: response.Table1[0]?.CASH || 0,
            charge: response.Table1[0]?.CHARGE || 0,
            discount: response.Table1[0]?.DISCOUNT || 0,
            gift: response.Table1[0]?.GIFT || 0,
            gross: response.Table1[0]?.GROSS || 0,
            loyalty: response.Table1[0]?.LOYALTY || 0,
            nettGross: response.Table1[0]?.NETTGROSS || 0,
            refundAmt: response.Table1[0]?.REFUNDAMT || 0,
            shipping: response.Table1[0]?.SHIPPING || 0,
            tax: response.Table1[0]?.TAX || 0,
            totNett: response.Table1[0]?.TOTNETT || 0,
            totPaid: response.Table1[0]?.TOTPAID || 0,
          });
        } else {
          console.error("Invalid API response: Table1 is missing");
        }
      } catch (error) {
        console.error("Error calculating order:", error);
      }
    };

    // Trigger when grossAmount or related state changes
    handleOrderCalculation();
  }, [orderDetailsData]);
  // API calls end

  return (
    <>
      {isComponentVisible && (
        <Box>
          {!orderDetails ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={"25vh"}
              sx={{ ...styles.AddStyledBox2 }}
            >
              <Typography
                variant="body2"
                // top={"50%"}
                textAlign={"center"}
                // alignContent={"center"}
                // justifyContent={"center"}
              >
                Select an order to view details
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box sx={{ ...styles.AddStyledBox2, pb: 2 }}>
              {" "}
              <Toolbar
                sx={{
                  maxWidth: "100%",
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                  background: theme.palette.background.paper,
                  // marginTop: {
                  //   xs: "170px",
                  //   md: "135px",
                  // },
                }}
              >
                {" "}
                <Box display={"flex"}>
                  <Typography
                    variant="h6"
                    id="tableTitle"
                    component="div"
                    sx={{
                      ...theme.typography.h2,
                      color: theme.palette.text.primary,
                      flex: "1 1 100%",
                      ml: 1,
                    }}
                  >
                    Order Details for :{" "}
                  </Typography>
                  <Typography
                    sx={{
                      ...theme.typography.h2,
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      ml: 1,
                    }}
                  >
                    {orderDetails.ORDERREFCODE}
                  </Typography>
                </Box>
              </Toolbar>
              <TableContainer
                sx={{
                  ...styles.VI_ViewStyledPaper,
                  maxWidth: {
                    md: "96%",
                    lg: "96%",
                    sm: "96%",
                    xs: "90%",
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{ background: theme.palette.background.paper }}
                    >
                      {/* <TableCell>#</TableCell> */}
                      <TableCell
                        sx={{
                          ...styles.AI_headtableCell,
                          textTransform: "capitalize",
                        }}
                      >
                        Item Name
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.AI_headtableCell,
                          textTransform: "capitalize",
                        }}
                        align="right"
                      >
                        Quantity
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.AI_headtableCell,
                          textTransform: "capitalize",
                        }}
                        align="right"
                      >
                        Rate
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.AI_headtableCell,
                          textTransform: "capitalize",
                        }}
                        align="right"
                      >
                        Amount
                      </TableCell>
                     
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow hover key={row.id}>
                        {/* <TableCell>{row.id}</TableCell> */}
                        <TableCell
                          sx={{
                            ...styles.AI_tablecell,
                          }}
                        >
                          {row.menuName}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.AI_tablecell,
                          }}
                          align="right"
                        >
                          {row.qty}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.AI_tablecell,
                          }}
                          align="right"
                        >
                          {new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(row.rate)}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.AI_tablecell,
                          }}
                          align="right"
                        >
                          {new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(row.amount)}
                        </TableCell>
                        
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ ...styles.right_align, mt: 2, mr: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  sx={{ ...styles.btn_primary, width: "20%" }}
                >
                  {loading ? "Placing Order..." : "Approve"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity} // Dynamic severity (success or error)
      />
    </>
  );
}

export default OfficerManageOrders;
